import {
  EnrichedEventData,
  MultipleTicketsPriceData,
  MultipleTicketsPriceDataInner,
  MultipleTicketsUIObjectData,
  OrderData,
  OrderPageData,
  TicketData,
  TimeAvailabilityDisabledReason,
  TimeSlotResponseOut,
} from '../api';
import { MULTIPLE_TICKETS_DETAILS_FIELD_NAME } from './OrderUtils';

export { MULTIPLE_TICKETS_DETAILS_FIELD_NAME };

export const MULTIPLE_TICKETS_UI_OBJECT_TYPE = 'multipleTickets';
export const MULTIPLE_TICKETS_PRICE_TYPE = 'multiple_tickets_price';

export const generateTicketId = (): string => crypto.randomUUID();

export type MultipleTicketsQuantities = Record<string, number>;

export function findMultipleTicketsUiObject(orderPage?: OrderPageData): MultipleTicketsUIObjectData | undefined {
  if (!orderPage?.objects) return undefined;
  return orderPage.objects.find((o) => o.type === MULTIPLE_TICKETS_UI_OBJECT_TYPE) as
    | MultipleTicketsUIObjectData
    | undefined;
}

export function findMultipleTicketsPrice(orderPage?: OrderPageData): MultipleTicketsPriceData | undefined {
  if (!orderPage?.price) return undefined;
  return orderPage.price.find((p) => p.type === MULTIPLE_TICKETS_PRICE_TYPE) as MultipleTicketsPriceData | undefined;
}

export function readMultipleTicketsQuantities(customDetails?: Record<string, any> | null): MultipleTicketsQuantities {
  const raw = customDetails?.[MULTIPLE_TICKETS_DETAILS_FIELD_NAME];
  return raw && typeof raw === 'object' ? (raw as MultipleTicketsQuantities) : {};
}

export function ticketIsArchived(ticket: Pick<TicketData, 'archived'>): boolean {
  return ticket.archived === true;
}

export function ticketCountsTowardCapacity(ticket: Pick<TicketData, 'counts_toward_total_capacity'>): boolean {
  return ticket.counts_toward_total_capacity !== false;
}

export function activeTickets(uiObject: MultipleTicketsUIObjectData): TicketData[] {
  return (uiObject.tickets || []).filter((t) => !ticketIsArchived(t));
}

export function activePriceOptions(price?: MultipleTicketsPriceData): MultipleTicketsPriceDataInner[] {
  return (price?.options || []).filter((opt) => opt.archived !== true);
}

export function sumCountingTickets(
  quantities: MultipleTicketsQuantities,
  uiObject: MultipleTicketsUIObjectData,
): number {
  return activeTickets(uiObject).reduce((total, ticket) => {
    if (!ticketCountsTowardCapacity(ticket)) return total;
    return total + (quantities[ticket.id] || 0);
  }, 0);
}

export function sumAllTickets(quantities: MultipleTicketsQuantities): number {
  return Object.values(quantities).reduce((sum, v) => sum + (v || 0), 0);
}

export function getPickerRowMax(args: {
  ticket: TicketData;
  currentValue: number;
  countingTotalSoFar: number;
  uiObject: MultipleTicketsUIObjectData;
}): number | undefined {
  const { ticket, currentValue, countingTotalSoFar, uiObject } = args;
  const capacityCap = typeof ticket.capacity === 'number' ? ticket.capacity : Infinity;

  if (!ticketCountsTowardCapacity(ticket)) {
    return capacityCap === Infinity ? undefined : capacityCap;
  }

  const maxTotal = uiObject.max_total_participants;
  const seatBudget =
    typeof maxTotal === 'number' && maxTotal > 0 ? maxTotal - (countingTotalSoFar - currentValue) : Infinity;

  const rowMax = Math.min(capacityCap, seatBudget);
  return rowMax === Infinity ? undefined : Math.max(0, rowMax);
}

export function getPerTicketCapacityTicketId(slot: TimeSlotResponseOut): string | undefined {
  if (slot.timeDisableReason !== TimeAvailabilityDisabledReason.PER_TICKET_CAPACITY_EXCEEDED) return undefined;
  return slot.timeDisabledDetails?.ticketTypeIdUnderCapacity;
}

export type TicketBreakdownEntry = {
  ticketId: string;
  label: string;
  quantity: number;
  counts: boolean;
  archived: boolean;
};

export function getTicketBreakdown(
  quantities: MultipleTicketsQuantities,
  uiObject: MultipleTicketsUIObjectData,
  language: string,
): TicketBreakdownEntry[] {
  const byId = new Map((uiObject.tickets || []).map((t) => [t.id, t]));
  const entries: TicketBreakdownEntry[] = Object.entries(quantities)
    .filter(([, qty]) => qty > 0)
    .map(([ticketId, qty]) => {
      const ticket = byId.get(ticketId);
      const label = ticket?.label?.[language as keyof typeof ticket.label] || ticket?.id || ticketId;
      return {
        ticketId,
        label: String(label),
        quantity: qty,
        counts: ticket ? ticketCountsTowardCapacity(ticket) : true,
        archived: ticket ? ticketIsArchived(ticket) : false,
      };
    });
  return entries.sort((a, b) => {
    if (a.archived !== b.archived) return a.archived ? 1 : -1;
    if (a.counts !== b.counts) return a.counts ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
}

export function orderHasMultipleTicketsBreakdown(order: OrderData, uiObject?: MultipleTicketsUIObjectData): boolean {
  if (!uiObject) return false;
  const q = readMultipleTicketsQuantities(order?.custom_details as Record<string, any> | undefined);
  return Object.values(q).some((v) => v > 0);
}

export function getCountingUsedTickets(
  event: Pick<EnrichedEventData, 'used_tickets' | 'multiple_ticket_usage'>,
  orderPage?: OrderPageData,
): number {
  const uiObject = findMultipleTicketsUiObject(orderPage);
  if (uiObject && event.multiple_ticket_usage) {
    return sumCountingTickets(event.multiple_ticket_usage, uiObject);
  }
  return event.used_tickets;
}

/** Used count the calendar shows for an event: counting tickets plus custom ticket usages. */
export const eventUsedTickets = (event: EnrichedEventData, orderPage?: OrderPageData): number =>
  getCountingUsedTickets(event, orderPage) +
  (event.custom_ticket_usages?.reduce((total, usage) => total + usage.used_tickets, 0) ?? 0);
